import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, CreditCard, PackageCheck, RefreshCw, Store as StoreIcon,
  MapPin, ShoppingBasket, Bike, Star, ChevronDown, Smartphone, Apple, Play, Carrot, Milk, Croissant,
  CupSoda, Cookie, Wheat, Sparkles, SprayCan, Rocket, IndianRupee } from "lucide-react";

const ICONS = { carrot: Carrot, milk: Milk, croissant: Croissant, "cup-soda": CupSoda, cookie: Cookie,
  wheat: Wheat, sparkles: Sparkles, "spray-can": SprayCan };

export const SectionHeader = ({ title, subtitle, onSeeAll, id }) => (
  <div className="mb-4 flex items-end justify-between gap-4" id={id}>
    <div>
      <h2 className="font-head text-xl font-extrabold tracking-tight text-slate-900 sm:text-2xl">{title}</h2>
      {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
    </div>
    {onSeeAll && (
      <button onClick={onSeeAll} className="flex shrink-0 items-center gap-1 text-sm font-semibold text-brand hover:gap-1.5">
        See all <ArrowRight className="h-4 w-4" />
      </button>
    )}
  </div>
);

export const CategoryNav = ({ cats }) => {
  const nav = useNavigate();
  return (
    <section data-testid="category-visual-nav" className="mb-10">
      <SectionHeader title="Shop by category" subtitle="Everything for your home, from stores nearby" onSeeAll={() => nav("/categories")} />
      <div className="grid grid-cols-4 gap-3 sm:grid-cols-6 lg:grid-cols-8">
        {(cats || []).map((c) => {
          const Icon = ICONS[c.icon] || Carrot;
          return (
            <button key={c.id} data-testid="category-tile" onClick={() => nav(`/category/${c.slug}`)}
              className="group flex flex-col items-center gap-2 rounded-2xl border border-slate-100 bg-white p-3 transition-all duration-200 hover:-translate-y-1 hover:border-brand/30 hover:shadow-md">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl transition-transform group-hover:scale-105"
                style={{ background: `${c.color}18`, color: c.color }}>
                <Icon className="h-7 w-7" />
              </div>
              <span className="line-clamp-2 text-center text-[11px] font-semibold text-slate-700">{c.name}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
};

const HOW = [
  { icon: MapPin, title: "Pick your neighbourhood store", desc: "Set your location and discover trusted local retailers around you." },
  { icon: ShoppingBasket, title: "Add daily essentials", desc: "Shop fresh groceries at your local store's real prices." },
  { icon: Bike, title: "Fast local delivery", desc: "Sit back while your order arrives fast from a store near you." },
];

export const HowItWorks = () => (
  <section data-testid="how-it-works-section" className="mb-12">
    <SectionHeader title="How Grofkart works" subtitle="Local shopping, three simple steps" />
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      {HOW.map((h, i) => (
        <div key={i} className="relative rounded-2xl border border-slate-200 bg-white p-6">
          <span className="absolute right-5 top-4 font-head text-4xl font-black text-slate-100">{i + 1}</span>
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-soft text-brand"><h.icon className="h-6 w-6" /></div>
          <h3 className="mt-4 font-head text-lg font-bold text-slate-900">{h.title}</h3>
          <p className="mt-1 text-sm leading-relaxed text-slate-500">{h.desc}</p>
        </div>
      ))}
    </div>
  </section>
);

const WHY = [
  { icon: ShieldCheck, title: "Trusted Local Retailers", desc: "Every store is verified before it goes live." },
  { icon: Truck, title: "Fast Local Delivery", desc: "Fresh orders delivered in 20–35 minutes." },
  { icon: CreditCard, title: "Secure Payments", desc: "UPI, cards, net banking & Cash on Delivery." },
  { icon: PackageCheck, title: "Genuine Products", desc: "100% authentic stock, straight from the shop." },
  { icon: RefreshCw, title: "Easy Returns", desc: "Quick refunds or replacements, no hassle." },
  { icon: StoreIcon, title: "Support Local Business", desc: "Every order helps a neighbourhood kirana thrive." },
];

export const WhyGrofkart = () => (
  <section data-testid="why-grofkart-section" className="mb-12">
    <SectionHeader title="Why Grofkart" subtitle="Local + trusted + fast + affordable" />
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {WHY.map((w, i) => (
        <div key={i} className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-md">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-soft text-amber-warm"><w.icon className="h-5 w-5" /></div>
          <div>
            <h3 className="font-head text-base font-bold text-slate-900">{w.title}</h3>
            <p className="mt-0.5 text-sm text-slate-500">{w.desc}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

export const LocalStory = () => (
  <section data-testid="local-store-identity-section" className="mb-12 overflow-hidden rounded-[28px] bg-brand-deep">
    <div className="grid items-center gap-6 p-8 sm:p-12 lg:grid-cols-2">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-amber-surge">Your Local Store, Now Online</p>
        <h2 className="mt-3 font-head text-2xl font-black leading-tight text-white sm:text-3xl lg:text-4xl">
          The shop around the corner, in your pocket.
        </h2>
        <p className="mt-4 max-w-lg text-sm leading-relaxed text-emerald-50/90 sm:text-base">
          Grofkart isn't a warehouse. It's your neighbourhood kirana, dairy and fresh market — digitised.
          Freshest morning stock, personal service, and the same fair prices you already trust.
        </p>
        <div className="mt-6 grid grid-cols-3 gap-4">
          {[["500+", "Local stores"], ["Fast", "Local delivery"], ["4.8★", "Customer rating"]].map(([a, b]) => (
            <div key={a}><p className="font-head text-2xl font-black text-white">{a}</p><p className="text-xs text-emerald-50/70">{b}</p></div>
          ))}
        </div>
      </div>
      <div className="hidden lg:block">
        <img src="/hero-grofkart.jpg" alt="Local store delivery" className="h-64 w-full rounded-2xl object-cover" />
      </div>
    </div>
  </section>
);

export const RetailerPartnerCTA = () => {
  const nav = useNavigate();
  return (
    <section data-testid="become-retailer-banner" className="mb-12 overflow-hidden rounded-[28px] border border-amber-border bg-amber-soft">
      <div className="flex flex-col items-start gap-5 p-8 sm:flex-row sm:items-center sm:justify-between sm:p-10">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-amber-warm">For shop owners</p>
          <h2 className="mt-2 font-head text-2xl font-black text-slate-900 sm:text-3xl">Digitise your shop in 15 minutes</h2>
          <p className="mt-2 max-w-lg text-sm text-slate-600">Expand your neighbourhood customer base with zero upfront tech fees. Manage orders, inventory and payments in one place.</p>
        </div>
        <button data-testid="partner-onboard-btn" onClick={() => nav("/partner")}
          className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-brand px-6 py-3.5 text-sm font-bold text-white transition-transform hover:scale-[1.03] active:scale-95">
          Become a Grofkart Partner <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </section>
  );
};

export const StartStoreBanner = () => {
  const nav = useNavigate();
  return (
    <section data-testid="start-store-banner" className="mb-12 overflow-hidden rounded-[28px] border border-slate-200">
      <div className="relative">
        <img src="/franchise-banner.jpg" alt="Start your Grofkart Store" className="h-72 w-full object-cover sm:h-80" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-deep/95 via-brand-deep/70 to-brand-deep/20" />
        <div className="absolute inset-0 flex flex-col justify-center gap-3 p-8 text-white sm:p-12">
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-amber-surge px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-900">
            <Rocket className="h-3.5 w-3.5" /> Franchise Opportunity
          </span>
          <h2 className="max-w-xl font-head text-3xl font-black leading-tight sm:text-5xl">Start Your Grofkart Store</h2>
          <p className="max-w-lg text-sm font-medium text-white/90 sm:text-lg">Bring Grofkart Quick Commerce to your area and own a branded store in your locality.</p>
          <p className="flex items-center gap-1.5 text-lg font-black sm:text-2xl">
            <IndianRupee className="h-5 w-5" />Investment Starts From ₹2,00,000/-<span className="text-sm font-medium">*</span>
          </p>
          <button data-testid="start-store-banner-btn" onClick={() => nav("/start-store")}
            className="mt-1 inline-flex w-fit items-center gap-2 rounded-xl bg-white px-6 py-3.5 text-sm font-bold text-brand-deep transition-transform hover:scale-[1.03] active:scale-95">
            Apply Now <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

export const DownloadApp = () => (
  <section data-testid="download-app-section" className="mb-12 rounded-[28px] border border-slate-200 bg-white p-8 sm:p-10">
    <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-soft text-brand"><Smartphone className="h-7 w-7" /></div>
        <div>
          <h2 className="font-head text-xl font-extrabold text-slate-900 sm:text-2xl">Get the Grofkart app</h2>
          <p className="mt-1 max-w-md text-sm text-slate-500">Install Grofkart on your phone for faster reorders, live tracking and app-only offers.</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-3">
        <span className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white"><Play className="h-4 w-4" /> Google Play</span>
        <span className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white"><Apple className="h-4 w-4" /> App Store</span>
      </div>
    </div>
  </section>
);

export const Testimonials = ({ items }) => {
  if (!items || !items.length) return null;
  return (
    <section data-testid="testimonials-section" className="mb-12">
      <SectionHeader title="Loved by neighbourhoods" subtitle="Real stories from customers & store owners" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((t) => (
          <div key={t.id} data-testid="testimonial-card" className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex gap-0.5 text-amber-surge">
              {Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-4 w-4 ${i < t.rating ? "fill-amber-surge" : "text-slate-200"}`} />)}
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-700">“{t.quote}”</p>
            <div className="mt-4 flex items-center gap-3">
              {t.avatar ? <img src={t.avatar} alt="" className="h-10 w-10 rounded-full object-cover" />
                : <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-soft font-bold text-brand">{t.name?.[0]}</div>}
              <div>
                <p className="text-sm font-bold text-slate-900">{t.name}</p>
                <p className="text-xs text-slate-400">{t.location}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export const FAQ = ({ items }) => {
  const [open, setOpen] = useState(0);
  if (!items || !items.length) return null;
  return (
    <section data-testid="faq-section" className="mb-12">
      <SectionHeader title="Frequently asked questions" />
      <div className="mx-auto max-w-3xl divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {items.map((f, i) => (
          <div key={f.id} data-testid="faq-item">
            <button onClick={() => setOpen(open === i ? -1 : i)} className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left">
              <span className="text-sm font-semibold text-slate-800">{f.question}</span>
              <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open === i ? "rotate-180" : ""}`} />
            </button>
            {open === i && <p className="px-5 pb-4 text-sm leading-relaxed text-slate-500">{f.answer}</p>}
          </div>
        ))}
      </div>
    </section>
  );
};

export const SiteFooter = () => {
  const nav = useNavigate();
  return (
    <footer data-testid="footer" className="mt-4 rounded-t-[28px] bg-slate-900 px-6 py-12 text-slate-300 sm:px-10">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 sm:grid-cols-4">
        <div className="col-span-2 sm:col-span-1">
          <p className="font-head text-xl font-black text-white">Grofkart</p>
          <p className="mt-2 text-sm text-slate-400">Your Local Store, Now Online. Hyperlocal quick-commerce powered by neighbourhood retailers.</p>
        </div>
        <div>
          <p className="mb-3 text-sm font-bold text-white">Shop</p>
          <ul className="space-y-2 text-sm">
            <li><button onClick={() => nav("/stores")} className="hover:text-white">Stores near you</button></li>
            <li><button onClick={() => nav("/categories")} className="hover:text-white">Categories</button></li>
            <li><button onClick={() => nav("/search")} className="hover:text-white">Search</button></li>
          </ul>
        </div>
        <div>
          <p className="mb-3 text-sm font-bold text-white">Partners</p>
          <ul className="space-y-2 text-sm">
            <li><button onClick={() => nav("/partner")} className="hover:text-white">Become a Retailer</button></li>
            <li><button onClick={() => nav("/register?role=delivery")} className="hover:text-white">Deliver with us</button></li>
            <li><button onClick={() => nav("/register?role=influencer")} data-testid="footer-influencer-link" className="hover:text-white">Become an Influencer</button></li>
            <li><button onClick={() => nav("/login")} className="hover:text-white">Partner login</button></li>
          </ul>
        </div>
        <div>
          <p className="mb-3 text-sm font-bold text-white">Company</p>
          <ul className="space-y-2 text-sm">
            <li><button onClick={() => nav("/support")} className="hover:text-white">Support</button></li>
            <li><button onClick={() => nav("/login")} className="hover:text-white">Admin</button></li>
          </ul>
        </div>
      </div>
      <div className="mx-auto mt-10 max-w-7xl border-t border-slate-800 pt-6 text-xs text-slate-500">
        <div className="flex flex-col items-center justify-between gap-2 sm:flex-row">
          <p>© {new Date().getFullYear()} Grofkart · Your Local Store, Now Online.</p>
          <p className="flex items-center gap-1.5 text-slate-400" data-testid="footer-ecosystem-badge">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-surge" /> Powered by the 99 ecosystem
          </p>
        </div>
      </div>
    </footer>
  );
};
